import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { manufacturingImages } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const DiamondManufacturingPage = () => {
  const shapes = [
    "Round Brilliant", "Princess Cut", "Emerald Cut", "Oval",
    "Pear Shaped", "Cushion Cut", "Marquise", "Asscher Cut",
    "Radiant Cut", "Heart Shape"
  ];

  const features = [
    {
      title: "Laser-Guided Precision",
      desc: "Every diamond is mapped with 3D scanning technology before a single facet is cut. Our proprietary alignment protocol ensures each angle maximizes brilliance and fire—down to a fraction of a degree."
    },
    {
      title: "Matched Pairs & Suites",
      desc: "Symmetry across a set is non-negotiable for fine jewelry. We hand-select and calibrate pairs by color, clarity, cut, and dimensions—delivering stones that are virtually indistinguishable from one another."
    },
    {
      title: "Expert Recutting",
      desc: "Breathe new life into heirloom or underperforming stones. Our recutting specialists enhance brilliance through modern faceting angles while preserving maximum carat weight—a delicate balance of art and geometry."
    },
    {
      title: "Calibrated Melee Supply",
      desc: "From 0.01 to 0.20 carats, our precision-calibrated melee diamonds are the backbone of pavé, channel, and micro-set designs. Tight size tolerances guarantee a seamless, uniform appearance in every setting."
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${manufacturingImages.diamond[1]})` }} />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Diamond Manufacturing
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Science and Soul in Every Stone
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}>Our Cutting Facility</h3>
                <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  From Rough to Radiant
                </h2>
                <p className="mt-6 text-[#555] text-[15px] leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Every great jewel begins with an extraordinary stone. Our diamond manufacturing
                  division combines state-of-the-art laser technology with the irreplaceable intuition
                  of veteran cutters to unlock each rough diamond's maximum potential.
                </p>
                <p className="mt-4 text-[#555] text-[15px] leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  We handle a spectrum from 0.03 to 5.00 carats, serving watch houses,
                  couture jewelry brands, and discerning retailers who accept nothing short
                  of optical perfection.
                </p>
              </div>
              <div className="relative">
                <img src={manufacturingImages.diamond[0]} alt="Diamond Manufacturing"
                  className="w-full h-[400px] object-cover" loading="lazy" />
                <div className="absolute -bottom-4 -left-4 w-20 h-20 border-2 border-[#c8a96e]" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Diamond Shapes */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-[42px] text-[#1a1a1a] font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Available Shapes
              </h2>
              <p className="mt-4 text-[#888] text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Classic rounds to exotic fancies—cut to uncompromising standards
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {shapes.map((shape, i) => (
              <FadeIn key={shape} delay={i * 0.05}>
                <div className="p-6 text-center bg-[#faf9f7] hover:bg-[#1a1a1a] group transition-all duration-400 cursor-default">
                  <span className="text-[14px] tracking-[0.1em] uppercase text-[#555] group-hover:text-white transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {shape}
                  </span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[#1a1a1a]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-20">
              <h2 className="text-3xl md:text-4xl lg:text-[42px] text-white font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Specialized Services
              </h2>
              <p className="mt-4 text-white/50 text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Capabilities built over decades of relentless refinement
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.1}>
                <div className="p-10 border border-white/10 hover:border-[#c8a96e]/30 transition-all duration-500">
                  <h3 className="text-2xl text-white mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-white/55 text-[14px] leading-[1.7]"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#faf9f7] text-center">
        <FadeIn>
          <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Looking for the Perfect Stone?
          </h2>
          <p className="mt-4 text-[#888] text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Tell us what you need—we'll source and cut it to your exact specifications.
          </p>
          <Link to="/contact-us"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3 border border-[#c8a96e] text-[#c8a96e] text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
};

export default DiamondManufacturingPage;
